import dayjs from 'dayjs';
import en from 'dayjs/locale/en-gb';
import ru from 'dayjs/locale/ru';
import tg from 'dayjs/locale/tg';
import uz from 'dayjs/locale/uz';
import uzLatn from 'dayjs/locale/uz-latn';
import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { IntlProvider } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useRoutes } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { useQueryParams } from 'use-query-params';

import { LOCALES } from 'common/shared-ui/src/i18n/locales';
import { messages } from 'common/shared-ui/src/i18n/messages';
import ErrorBoundary from 'components/ErrorBoundary';
import { UncaughtError } from 'components/ErrorBoundary/components/UncaughtError';

import packageJson from '../../../package.json';
import {
  getDefaultLocalization,
  getIssueRelated,
  getLocalization,
  getXlanceAuthToken,
} from '../../../packages/common/api';
import cookies from '../../../packages/common/utils/cookies';
import devRoutes from '../app/devRoutes';
import routes from '../app/routes';
import { getUser } from '../auth/actions';

import { setConfigurationFromWindow } from './redux/reducers/configuration';
import { setLocalization } from './redux/reducers/localization';

function AppContainer() {
  useLayoutEffect(() => {
    console.log(`Version: ${packageJson.version}`);
    console.log(`Host: ${window?.APP_CONFIG?.env?.API_HOST}`);
  }, []);

  const element = useRoutes(
    process.env.NODE_ENV === 'development' ? devRoutes : routes,
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const appLang = useSelector(state => state.user.language);
  const userInfo = useSelector(state => state.user.info);
  const isUserLoadingError = useSelector(state => state.user.error);
  const projectSelected = useSelector(state => state.user.project);
  const [searchParams, setSearchParams] = useSearchParams();
  const accessToken = cookies.getCookie('access_token');
  const [languageLoaded, setLanguageLoaded] = useState(false);
  const [query, setQuery] = useQueryParams({
    back_url: '',
    redmine_token: '',
    project: '',
    redmineOrigin: '',
  });
  const location = useLocation();
  const languageMap = useRef(
    new Map([
      ['ru', { lang: ru }],
      ['en', { lang: en }],
      ['uzb-lath', { lang: uzLatn }],
      ['uzb-cyrl', { lang: uz }],
      ['tg', { lang: tg }],
    ]),
  );

  useEffect(() => {
    let ignore = false;
    async function getAppLocalization() {
      let params = {};
      window.localStorage.getItem('lang') &&
        (params.languageCode = window.localStorage.getItem('lang'));
      const { data: response } = await getDefaultLocalization(params);
      if (!ignore) {
        return response;
      }
    }
    getAppLocalization()
      .then(res => {
        setLanguageLoaded(true);
        dispatch(setLocalization(res));
      })
      .catch(error => console.error(error, 'ERROR IN GET ISSUE'));
    return () => {
      ignore = true;
    };
  }, [dispatch, location.pathname]);

  // Set configuration from window.APP_CONFIG to Redux store
  useEffect(() => {
    dispatch(setConfigurationFromWindow());
  }, [dispatch]);

  useEffect(() => {
    if (location.key === 'default' && !location.pathname.includes('login')) {
      window.localStorage.setItem(
        'back_url',
        location.pathname == '/null'
          ? (location.pathname = '/')
          : location.pathname,
      );
    }
  }, [location]);
  const getXlanceToken = useCallback(async (back_url, redmine_token) => {
    try {
      const { data } = await getXlanceAuthToken({
        redmine_token: redmine_token.trim(),
      });
      cookies.setCookie({
        key: 'access_token',
        value: data.access_token,
        host: `${window.location.protocol}//${window.location.hostname}`,
      });
      return (window.location.href = `${back_url}${back_url.split('').includes('?') ? '&' : '?'}incoming_url=${
        window.location.origin
      }`);
    } catch (error) {
      console.error('CANNOT SET PASSTHROWGH TOKEN', error);
    }
  }, []);

  useEffect(() => {
    if (!accessToken && query.redmine_token && query.back_url) {
      getXlanceToken(query.back_url, query.redmine_token);
      return;
    }
    if (isUserLoadingError.length) {
      return navigate('/login');
    }
    if (!userInfo) {
      dispatch(getUser());
    }
  }, [
    appLang,
    dispatch,
    isUserLoadingError,
    navigate,
    searchParams,
    userInfo,
    query.redmineOrigin,
    getXlanceToken,
    query.back_url,
    query.redmine_token,
    accessToken,
  ]);

  useEffect(() => {
    if (window.localStorage.getItem('lang')) {
      let lang = languageMap.current.get(
        window.localStorage.getItem('lang'),
      ).lang;
      dayjs.locale(lang);
    }
  }, [appLang]);

  return (
    <IntlProvider
      messages={messages[appLang]}
      locale={appLang}
      defaultLocale={LOCALES.RUSSIAN}
    >
      <ErrorBoundary
        fallback={<UncaughtError />}
        resetKeys={[location.pathname]}
      >
        {languageLoaded && element}
      </ErrorBoundary>
    </IntlProvider>
  );
}

export default memo(AppContainer);
